import { Inbox, Loader2, Trash2, Phone, Mail, Search, CalendarDays, Clock } from "lucide-react";

export const STATUS = {
  new: { label: "New", cls: "bg-accent/10 text-accent" },
  contacted: { label: "Contacted", cls: "bg-amber-100 text-amber-700" },
  completed: { label: "Completed", cls: "bg-emerald-100 text-emerald-700" },
};
export const NEXT = { new: "contacted", contacted: "completed", completed: "new" };

export const BookingsPanel = ({
  visible, filter, setFilter, q, setQ, hasMore, loadingMore, loadMore, onCycle, onRemove,
}) => (
  <>
    {/* Filters */}
    <div className="mt-6 flex flex-col sm:flex-row gap-4 sm:items-center justify-between">
      <div className="flex gap-2 flex-wrap">
        {["all", "new", "contacted", "completed"].map((f) => (
          <button
            key={f}
            data-testid={`filter-${f}`}
            onClick={() => setFilter(f)}
            className={`h-11 px-5 rounded-full font-semibold capitalize transition-colors ${
              filter === f
                ? "bg-primary text-white"
                : "bg-card border border-border text-slate-300 hover:border-accent"
            }`}
          >
            {f}
          </button>
        ))}
      </div>
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
        <input
          data-testid="admin-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search name, email, service…"
          className="h-12 w-full sm:w-80 rounded-full border border-border bg-card pl-11 pr-4 outline-none focus:border-accent text-foreground"
        />
      </div>
    </div>

    {/* Bookings list */}
    <div className="mt-6">
      {visible.length === 0 ? (
        <div data-testid="admin-empty" className="rounded-2xl border border-dashed border-border bg-card p-16 text-center">
          <Inbox className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="font-heading font-bold text-xl text-foreground">No bookings here yet</p>
          <p className="mt-2 text-muted-foreground">New requests from your website will appear on this page.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {visible.map((b) => {
            const st = STATUS[b.status] || STATUS.new;
            return (
              <div
                key={b.id}
                data-testid={`booking-${b.id}`}
                className="rounded-2xl border border-border bg-card p-6 grid lg:grid-cols-[1fr,auto] gap-5"
              >
                <div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <h3 className="font-heading font-bold text-xl text-foreground">{b.name}</h3>
                    <span className={`rounded-full px-3 py-1 text-xs font-bold ${st.cls}`}>{st.label}</span>
                    {b.service && (
                      <span className="rounded-full bg-muted px-3 py-1 text-xs font-semibold text-muted-foreground">
                        {b.service}
                      </span>
                    )}
                  </div>
                  <p className="mt-3 text-slate-300 leading-relaxed">{b.message}</p>
                  <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm text-muted-foreground">
                    <a href={`tel:${b.phone}`} className="inline-flex items-center gap-2 hover:text-accent">
                      <Phone className="h-4 w-4" /> {b.phone}
                    </a>
                    <a href={`mailto:${b.email}`} className="inline-flex items-center gap-2 hover:text-accent">
                      <Mail className="h-4 w-4" /> {b.email}
                    </a>
                    {b.preferred_date && (
                      <span className="inline-flex items-center gap-2 text-accent font-semibold">
                        <CalendarDays className="h-4 w-4" /> {b.preferred_date}
                        {b.preferred_time ? ` · ${b.preferred_time}` : ""}
                      </span>
                    )}
                    <span className="inline-flex items-center gap-2 text-muted-foreground">
                      <Clock className="h-4 w-4" /> {new Date(b.created_at).toLocaleString()}
                    </span>
                  </div>
                </div>
                <div className="flex lg:flex-col gap-2 justify-end">
                  <button
                    data-testid={`booking-status-${b.id}`}
                    onClick={() => onCycle(b)}
                    className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-full bg-accent/10 text-accent font-bold hover:bg-accent hover:text-white transition-colors whitespace-nowrap"
                  >
                    Mark {STATUS[NEXT[b.status] || "new"].label}
                  </button>
                  <button
                    data-testid={`booking-delete-${b.id}`}
                    onClick={() => onRemove(b)}
                    className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-full border border-border text-muted-foreground font-semibold hover:border-destructive hover:text-destructive transition-colors"
                  >
                    <Trash2 className="h-4 w-4" /> Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {hasMore && !q && filter === "all" && (
        <div className="mt-6 text-center">
          <button
            data-testid="admin-load-more"
            onClick={loadMore}
            disabled={loadingMore}
            className="inline-flex items-center gap-2 h-12 px-8 rounded-full border border-border bg-card font-semibold text-foreground hover:border-accent transition-colors disabled:opacity-60"
          >
            {loadingMore ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
            Load more bookings
          </button>
        </div>
      )}
    </div>
  </>
);
